import { useMemo } from 'react';
import {
  Frame,
  SageClassnames,
  useSageBreakpoint,
  SageTokens,
  Icon,
} from '@kajabi/sage-react';

interface TransformedUnsplashObject {
  image_url: string;
  alt_description?: string;
}

interface PropsType {
  userData: any;
  previewImageUrls: TransformedUnsplashObject[];
  heroImageUrl: string;
}

const PodcastPreview = ({
  userData,
  previewImageUrls,
  heroImageUrl,
}: PropsType) => {
  const podcastData = useMemo(() => {
    return userData.ai_data.podcasts;
  }, [userData, previewImageUrls]);

  const breakpoints = useSageBreakpoint();

  return (
    <Frame
      style={{
        width: breakpoints.lg ? '100%' : '70%',
        border: breakpoints.sm ? '1rem solid #eceeef' : null,
        backgroundColor: breakpoints.sm ? '#eceeef' : null,
        borderRadius: breakpoints.sm ? '32px' : '15px',
        boxShadow: breakpoints.sm ? 'rgba(0, 0, 0, 0.1) 0px 20px 40px' : null,
      }}
      align={Frame.ALIGNMENTS.STRETCH_CENTER}
    >
      <Frame
        gap={Frame.GAPS.NONE}
        className={`container ${
          SageClassnames.SPACERS[breakpoints.md ? 'NONE' : 'LG_TOP']
        }`}
        style={{
          width: [breakpoints.md ? '100%' : '732px'],
          boxShadow: breakpoints.sm ? null : 'rgba(0, 0, 0, 0.1) 0px 20px 40px',
          borderRadius: breakpoints.sm ? '24px' : '15px',
          height: breakpoints.md ? '576px' : '720px',
          overflowY: 'scroll',
        }}
      >
        {/* Mock window component with 3 buttons */}
        {breakpoints.sm || (
          <Frame
            background="#ECEEEF"
            style={{
              height: '42px',
              width: breakpoints.md ? '652px' : '896px',
              borderTopLeftRadius: '15px',
              borderTopRightRadius: '15px',
            }}
          >
            <Frame
              direction={Frame.DIRECTIONS.HORIZONTAL}
              padding={Frame.PADDINGS.SM}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
            </Frame>
          </Frame>
        )}

        {/* Podcast navigation component */}
        <Frame
          background="#FFFFFF"
          style={{ height: '61px' }}
          direction={Frame.DIRECTIONS.HORIZONTAL}
          padding={Frame.PADDINGS.SM}
        >
          <Frame
            padding={Frame.PADDINGS.XS}
            direction={Frame.DIRECTIONS.HORIZONTAL}
          >
            <div
              style={{
                width: 87,
                height: breakpoints.sm ? 8 : 12,
                backgroundColor: '#D3D5D9',
                borderRadius: '100px',
              }}
            ></div>
          </Frame>
          {breakpoints.sm ? (
            <Frame
              padding={Frame.PADDINGS.NONE}
              align={Frame.ALIGNMENTS.TOP_RIGHT}
              gap={Frame.GAPS.XXS}
            >
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
            </Frame>
          ) : (
            <Frame
              padding={Frame.PADDINGS.XS}
              align={Frame.ALIGNMENTS.TOP_RIGHT}
              direction={Frame.DIRECTIONS.HORIZONTAL}
            >
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
            </Frame>
          )}
        </Frame>

        {/* Podcast hero image component */}
        <Frame
          style={{
            minHeight: breakpoints.sm ? '345px' : '214px',
            backgroundImage: `
                          linear-gradient(rgba(0,0,0,.65), rgba(0,0,0,.65)),
                          url(${heroImageUrl}})
                      `,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
          padding={Frame.PADDINGS.SM}
          gap={Frame.GAPS.XS}
          align={Frame.ALIGNMENTS.CENTER_CENTER}
        >
          <Frame
            gap={Frame.GAPS.SM}
            align={Frame.ALIGNMENTS[breakpoints.sm ? 'null' : 'CENTER_CENTER']}
            direction={
              Frame.DIRECTIONS[breakpoints.sm ? 'VERTICAL' : 'HORIZONTAL']
            }
          >
            <Frame
              style={{
                height: breakpoints.sm ? '119px' : '150px',
                width: breakpoints.sm ? '119px' : '155px',
                backgroundImage: `
                          url(${previewImageUrls.slice(-2)[0]?.image_url}})
                      `,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                borderRadius: '8px',
              }}
              padding={Frame.PADDINGS.LG}
              gap={Frame.GAPS.XS}
              align={Frame.ALIGNMENTS.CENTER_CENTER}
            ></Frame>
            <Frame
              gap={Frame.GAPS.XS}
              style={{
                maxWidth: breakpoints.sm ? '100%' : '440px',
                height: breakpoints.sm ? '119px' : '150px',
              }}
              direction={Frame.DIRECTIONS.VERTICAL}
            >
              <h5
                style={{
                  fontSize: breakpoints.md ? 24 : 20,
                  fontWeight: 700,
                }}
                className={`${SageClassnames.TYPE.HEADING_5} ${SageClassnames.TYPE_COLORS.WHITE}`}
              >
                {podcastData.title}
              </h5>
              <div
                style={{
                  width: 105,
                  height: 8,
                  backgroundColor: '#DBE3EF',
                  borderRadius: '100px',
                }}
              ></div>
              <p
                style={{
                  fontSize: breakpoints.md ? 14 : 12,
                  fontWeight: 500,
                  lineHeight: '16px',
                }}
                className={`${SageClassnames.TYPE.BODY_SMALL_MED} ${SageClassnames.TYPE_COLORS.WHITE}`}
              >
                {podcastData.description}
              </p>
              <div
                style={{
                  width: 83,
                  height: 28,
                  backgroundColor: '#0072EF',
                  borderRadius: '100px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                className={
                  SageClassnames.SPACERS[breakpoints.sm ? 'SM_TOP' : 'XS_TOP']
                }
              >
                <p style={{ fontSize: '12px', color: 'white', margin: 'auto' }}>
                  Listen now
                </p>
              </div>
            </Frame>
          </Frame>
        </Frame>

        {/* Podcast sessions component */}
        <Frame
          padding={Frame.PADDINGS[breakpoints.sm ? 'SM' : 'MD']}
          align={Frame.ALIGNMENTS.CENTER_CENTER}
          style={{ backgroundColor: 'white' }}
        >
          <div>
            <p
              className={`${SageClassnames.TYPE.BODY_BOLD} ${SageClassnames.SPACERS.XS_BOTTOM}`}
            >
              Latest episode
            </p>
            <Frame
              style={{
                height: breakpoints.sm ? '174px' : '150px',
                width: breakpoints.sm ? '278px' : '398px',
                border: '1px solid #ECEEEF',
                borderRadius: '10px',
                justifyContent: 'space-between',
                background: SageTokens.COLOR_PALETTE.PRIMARY_300,
              }}
              gap={Frame.GAPS.SM}
              padding={Frame.PADDINGS.SM}
              direction={Frame.DIRECTIONS.HORIZONTAL}
            >
              <Frame
                style={{
                  height: '70px',
                  maxWidth: '70px',
                  backgroundImage: `
                          url(${previewImageUrls[0]?.image_url}})
                      `,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  borderRadius: '8px',
                }}
                padding={Frame.PADDINGS.SM}
                gap={Frame.GAPS.XS}
                align={Frame.ALIGNMENTS.CENTER_CENTER}
              ></Frame>
              <Frame gap={Frame.GAPS.XXS} direction={Frame.DIRECTIONS.VERTICAL}>
                <p
                  className={`${SageClassnames.TYPE.BODY_XSMALL_BOLD} ${SageClassnames.TYPE_COLORS.PURPLE_200}`}
                >
                  1 day ago
                </p>
                <h5
                  className={`${SageClassnames.TYPE.BODY_SMALL_BOLD} ${SageClassnames.TYPE_COLORS.WHITE}`}
                >
                  {podcastData.episodes[0].title}
                </h5>
                <p
                  className={`${SageClassnames.TYPE.BODY_XSMALL} ${SageClassnames.TYPE_COLORS.WHITE}`}
                >
                  {breakpoints.sm
                    ? podcastData.episodes[0].description.slice(0, 50).trim() +
                      '...'
                    : podcastData.episodes[0].description}
                </p>
                <div
                  style={{
                    width: 83,
                    height: 28,
                    backgroundColor: 'white',
                    borderRadius: '100px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  className={SageClassnames.SPACERS.XS_TOP}
                >
                  <p
                    style={{
                      fontSize: '12px',
                      color: SageTokens.COLOR_PALETTE.PRIMARY_300,
                      margin: 'auto',
                    }}
                  >
                    <Icon size={Icon.SIZES.SM} icon={Icon.ICONS.PLAY}></Icon>
                  </p>
                </div>
              </Frame>
            </Frame>
          </div>
          <div>
            <p
              className={`${SageClassnames.TYPE.BODY_BOLD} ${SageClassnames.SPACERS.XS_BOTTOM}`}
            >
              More episodes
            </p>
            <Frame
              style={{
                height: breakpoints.sm ? '174px' : '150px',
                width: breakpoints.sm ? '278px' : '398px',
                border: '1px solid #ECEEEF',
                borderRadius: '10px',
                justifyContent: 'space-between',
                background: SageTokens.COLOR_PALETTE.GREY_300,
              }}
              gap={Frame.GAPS.SM}
              padding={Frame.PADDINGS.SM}
              direction={Frame.DIRECTIONS.HORIZONTAL}
              className={SageClassnames.SPACERS.SM_BOTTOM}
            >
              <Frame
                style={{
                  height: '70px',
                  maxWidth: '70px',
                  backgroundImage: `
                          url(${previewImageUrls[0]?.image_url}})
                      `,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  borderRadius: '8px',
                }}
                padding={Frame.PADDINGS.SM}
                gap={Frame.GAPS.XS}
                align={Frame.ALIGNMENTS.CENTER_CENTER}
              ></Frame>
              <Frame gap={Frame.GAPS.XXS} direction={Frame.DIRECTIONS.VERTICAL}>
                <p
                  className={`${SageClassnames.TYPE.BODY_XSMALL_BOLD} ${SageClassnames.TYPE_COLORS.CHARCOAL_200}`}
                >
                  5 days ago
                </p>
                <h5
                  className={`${SageClassnames.TYPE.BODY_SMALL_BOLD} ${SageClassnames.TYPE_COLORS.CHARCOAL_400}`}
                >
                  {podcastData.episodes[1].title}
                </h5>
                <p
                  className={`${SageClassnames.TYPE.BODY_XSMALL} ${SageClassnames.TYPE_COLORS.CHARCOAL_400}`}
                >
                  {breakpoints.sm
                    ? podcastData.episodes[1].description.slice(0, 50).trim() +
                      '...'
                    : podcastData.episodes[1].description}
                </p>
                <div
                  style={{
                    width: 83,
                    height: 28,
                    backgroundColor: 'white',
                    borderRadius: '100px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  className={SageClassnames.SPACERS.XS_TOP}
                >
                  <p
                    style={{
                      fontSize: '12px',
                      color: SageTokens.COLOR_PALETTE.PRIMARY_300,
                      margin: 'auto',
                    }}
                  >
                    <Icon size={Icon.SIZES.SM} icon={Icon.ICONS.PLAY}></Icon>
                  </p>
                </div>
              </Frame>
            </Frame>
            <Frame
              style={{
                height: breakpoints.sm ? '174px' : '150px',
                width: breakpoints.sm ? '278px' : '398px',
                border: '1px solid #ECEEEF',
                borderRadius: '10px',
                justifyContent: 'space-between',
                background: SageTokens.COLOR_PALETTE.GREY_300,
              }}
              gap={Frame.GAPS.SM}
              padding={Frame.PADDINGS.SM}
              direction={Frame.DIRECTIONS.HORIZONTAL}
            >
              <Frame
                style={{
                  height: '70px',
                  maxWidth: '70px',
                  backgroundImage: `
                          url(${previewImageUrls[0]?.image_url}})
                      `,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  borderRadius: '8px',
                }}
                padding={Frame.PADDINGS.SM}
                gap={Frame.GAPS.XS}
                align={Frame.ALIGNMENTS.CENTER_CENTER}
              ></Frame>
              <Frame gap={Frame.GAPS.XXS} direction={Frame.DIRECTIONS.VERTICAL}>
                <p
                  className={`${SageClassnames.TYPE.BODY_XSMALL_BOLD} ${SageClassnames.TYPE_COLORS.CHARCOAL_200}`}
                >
                  12 days ago
                </p>
                <h5
                  className={`${SageClassnames.TYPE.BODY_SMALL_BOLD} ${SageClassnames.TYPE_COLORS.CHARCOAL_400}`}
                >
                  {podcastData.episodes[2].title}
                </h5>
                <p
                  className={`${SageClassnames.TYPE.BODY_XSMALL} ${SageClassnames.TYPE_COLORS.CHARCOAL_400}`}
                >
                  {breakpoints.sm
                    ? podcastData.episodes[2].description.slice(0, 50).trim() +
                      '...'
                    : podcastData.episodes[2].description}
                </p>
                <div
                  style={{
                    width: 83,
                    height: 28,
                    backgroundColor: 'white',
                    borderRadius: '100px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  className={SageClassnames.SPACERS.XS_TOP}
                >
                  <p
                    style={{
                      fontSize: '12px',
                      color: SageTokens.COLOR_PALETTE.PRIMARY_300,
                      margin: 'auto',
                    }}
                  >
                    <Icon size={Icon.SIZES.SM} icon={Icon.ICONS.PLAY}></Icon>
                  </p>
                </div>
              </Frame>
            </Frame>
          </div>
        </Frame>
      </Frame>
    </Frame>
  );
};

export default PodcastPreview;
